import { FileText, Mail, Plus, Search, X } from 'lucide-react'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { CreateBusiness, GetCurrency, UpdateBusiness } from '~/api'
import { Button } from '~/components/Common/Button'
import { Toggle } from '~/components/Common/Toggle'
import Icon from '~/components/Icon'
import { ImageUploader } from '~/components/Image/ImageUploader'
import { Modal } from '~/components/Modal/Modal'
import $posthog from '~/lib/posthog'
import { BusinessI, CurrencyI } from '~/types'
import { getCurrentBusiness } from '~/utils'
import { useGlobal } from '../Common/GlobalDataProvider'
import CountrySelect from '../SelectCountry/SelectCountry'

interface PopupBusinessFormI {
    show: boolean
    data?: BusinessI
    onSave: (value: BusinessI) => void
    onClose: () => void
    localeText: any
}

export const PopupBusinessForm = ({ show, data, onSave, onClose, localeText }: PopupBusinessFormI) => {
    const router = useRouter()
    const isEdit = !!data
    const { toastProps, setToastProps } = useGlobal()
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState<Record<string, string>>({})

    // ── Form state ───────────────────────────────────────────────
    const [field, setField] = useState<BusinessI>(() => {
        if (isEdit) return data
        return {
            access: [],
            businessId: null,
            roles: 'admin',
            category: '',
            imageUrl: '',
            name: '',
            attributes: [],
            countryCode: '',
            currencyCode: '',
            isNegativeTransactionDisabled: false
        }
    })

    useEffect(() => {
        if (show) {
            if (data) {
                setField(data)
            } else {
                setField({
                    access: [],
                    businessId: null,
                    roles: 'owner',
                    category: '',
                    imageUrl: '',
                    name: '',
                    attributes: [],
                    countryCode: '',
                    currencyCode: '',
                    isNegativeTransactionDisabled: false
                })
            }
        }
    }, [show, data])


    // ── Currency picker state ────────────────────────────────────
    const [currencies, setCurrencies] = useState<CurrencyI[]>([])
    const [currencySearch, setCurrencySearch] = useState('')
    const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false)
    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyI | null>(null)
    const currencyRef = useRef<HTMLDivElement>(null)

    // ── Load currencies when modal opens ─────────────────────────
    useEffect(() => {
        if (!show) return
        const fetchCurrencies = async () => {
            const businessId = Number(getCurrentBusiness())
            // For new business there's no businessId yet — use 0 as fallback (global list)
            const { data: currData } = await GetCurrency(businessId || 0, localeText, { limit: 200, offset: 0, search: '' })
            if (currData) setCurrencies(currData)
        }
        fetchCurrencies()
    }, [show])

    // Prefill selected currency when editing
    useEffect(() => {
        if (isEdit && data?.currency && currencies.length) {
            const match = currencies.find(c => c.currency_code === data.currencyCode || c.currency_symbol === data.currency?.symbol)
            if (match) setSelectedCurrency(match)
        }
    }, [currencies, isEdit])

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (currencyRef.current && !currencyRef.current.contains(e.target as Node)) {
                setShowCurrencyDropdown(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const filteredCurrencies = useMemo(() => {
        const q = currencySearch.toLowerCase()
        return currencies.filter(c =>
            c.currency_name?.toLowerCase().includes(q) ||
            c.currency_code?.toLowerCase().includes(q) ||
            c.currency_symbol?.toLowerCase().includes(q)
        )
    }, [currencies, currencySearch])

    // ── Attribute helpers ────────────────────────────────────────
    const addAttribute = () => {
        setField({ ...field, attributes: [...(field.attributes || []), { key: '', value: '' }] })
    }

    const updateAttribute = (index: number, key: string, value: string) => {
        const updated = [...(field.attributes || [])]
        updated[index] = { key, value }
        setField({ ...field, attributes: updated })
    }

    const removeAttribute = (index: number) => {
        const updated = [...(field.attributes || [])]
        updated.splice(index, 1)
        setField({ ...field, attributes: updated })
    }

    // ── Submit ───────────────────────────────────────────────────
    const validate = () => {
        const newErrors: Record<string, string> = {}
        if (!field.name?.trim()) newErrors.name = 'Business name is required'
        if (!field.countryCode) newErrors.countryCode = 'Country is required'
        if (!isEdit && !selectedCurrency) newErrors.currency = 'Currency is required'

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const submit = async () => {
        if (loading) return
        if (!validate()) return

        setLoading(true)

        const payload: any = {
            countryCode: field.countryCode,
            category: field.category,
            imageUrl: field.imageUrl || null,
            name: field.name,
            attributes: field.attributes || [],
            address: (field as any).address || undefined,
            phoneNumber: (field as any).phoneNumber || undefined,
            additionalPhoneNumber: (field as any).additionalPhoneNumber || [],
            email: (field as any).email || undefined,
            isNegativeTransactionDisabled: field.isNegativeTransactionDisabled || false,
            ...(selectedCurrency ? {
                currencyId: selectedCurrency.currency_code,   // e.g. "USD"
                currency: {
                    id: selectedCurrency.currencyId,
                    symbol: selectedCurrency.currency_symbol
                }
            } : {})
        }

        const { data: business, error } = isEdit
            ? await UpdateBusiness(Number(getCurrentBusiness()), payload, localeText)
            : await CreateBusiness(payload, localeText)

        if (error) {
            setLoading(false)
            setToastProps({ ...toastProps, message: error?.message || localeText?.common?.request_error, type: 'error', open: true, wrapper: 'right-hand-side' })
            return
        }

        setToastProps({ ...toastProps, message: localeText?.common?.message?.successfully_saved, type: 'success', open: true, wrapper: 'right-hand-side' })
        setLoading(false)

        const eventProps = {
            business_name: field?.name,
            business_category: field?.category,
            business_logo: field?.imageUrl?.length ? 'yes' : 'no',
            business_id: isEdit ? data.businessId : (business as BusinessI)?.businessId
        }
        $posthog.capture('business_create_submit', eventProps)

        onSave(isEdit ? data : (business as BusinessI))
    }

    // Attribute preview lines
    const previewLines = (field.attributes || []).filter(a => a.key?.trim() && a.value?.trim())

    return (
        <Modal
            size="lg"
            show={show}
            title={isEdit ? localeText?.business?.edit_business?.title : localeText?.business?.add_business?.title || 'Create a Business'}
            close
            onClose={onClose}
            action={
                <Button
                    text={localeText?.common?.button?.submit || 'Continue'}
                    icon={<Icon name="save" size="lg" />}
                    loading={loading}
                    disabled={!field.name?.trim() || !field.countryCode || (!isEdit && !selectedCurrency)}
                    onClick={submit}
                />
            }
        >
            <div className="flex flex-col gap-3 w-full pb-4">

                {/* ── Logo ─────────────────────────────── */}
                <div className="flex justify-start">
                    <ImageUploader
                        localeText={localeText}
                        image={field.imageUrl}
                        setImage={(value) => setField({ ...field, imageUrl: value })}
                        containerName="business"
                    />
                </div>

                {/* ── Business Name ─────────────────────── */}
                <div>
                    <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                        {localeText?.business?.add_business?.name || 'Business Name'} <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={field.name}
                        onChange={e => {
                            setField({ ...field, name: e.target.value })
                            if (errors.name) setErrors({ ...errors, name: '' })
                        }}
                        placeholder={localeText?.business?.add_business?.name_placeholder || 'Enter business name'}
                        className={`w-full h-[40px] px-3 text-[14px] border border-gray-200 rounded-lg outline-none focus:border-[#FF5200] focus:ring-1 focus:ring-[#FF5200]/20 transition-all bg-white ${errors.name ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : ''}`}
                    />
                    {errors.name && <p className="text-[11px] text-red-500 mt-1 ml-1 font-medium flex items-center gap-1">
                        <span>*</span> {errors.name}
                    </p>}
                </div>

                {/* ── Country + Currency (2-col row) ────── */}
                <div className={`grid ${!isEdit ? "grid-cols-2" : "grid-cols-1"}  gap-6`}>

                    {/* Country */}
                    <div>
                        <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                            {localeText?.business?.add_business?.country || 'Country'} <span className="text-red-500">*</span>
                        </label>
                        <CountrySelect
                            value={field.countryCode}
                            error={!!errors.countryCode}
                            onChange={(value) => {
                                setField({ ...field, countryCode: value })
                                if (errors.countryCode) setErrors({ ...errors, countryCode: '' })
                            }}
                        />
                        {errors.countryCode && <p className="text-[11px] text-red-500 mt-1 ml-1 font-medium flex items-center gap-1">
                            <span>*</span> {errors.countryCode}
                        </p>}
                    </div>

                    {/* Currency */}
                    {
                        !isEdit &&

                        <div ref={currencyRef} className="relative">
                            <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                                {localeText?.business?.add_business?.currency || 'Select Currency'} <span className="text-red-500">*</span>
                            </label>
                            <button
                                type="button"
                                onClick={() => setShowCurrencyDropdown(v => !v)}
                                className={`w-full h-[42px] flex items-center gap-3 px-3 border rounded-md bg-white text-left hover:border-[#FF5200] transition-colors ${errors.currency ? 'border-red-400' : 'border-gray-200'}`}
                            >
                                <span className="text-[#FF5200]">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v12M8 12h8" /></svg>
                                </span>
                                <span className={`flex-1 text-[14px] ${selectedCurrency ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                                    {selectedCurrency
                                        ? `${selectedCurrency.currency_name} (${selectedCurrency.currency_symbol})`
                                        : 'Select currency'}
                                </span>
                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${showCurrencyDropdown ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" /></svg>
                            </button>
                            {errors.currency && (
                                <p className="text-[11px] text-red-500 mt-1 ml-1 font-medium flex items-center gap-1">
                                    <span>*</span> {errors.currency}
                                </p>
                            )}

                            {showCurrencyDropdown && (
                                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                                    {/* Search */}
                                    <div className="px-3 pt-3 pb-2 border-b border-gray-100">
                                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                                            <Search size={15} className="text-gray-400 shrink-0" />
                                            <input
                                                autoFocus
                                                type="text"
                                                value={currencySearch}
                                                onChange={e => setCurrencySearch(e.target.value)}
                                                placeholder="Search currencies..."
                                                className="flex-1 bg-transparent text-[13px] outline-none text-gray-700 placeholder:text-gray-400"
                                            />
                                        </div>
                                    </div>
                                    {/* List */}
                                    <div className="max-h-52 overflow-y-auto">
                                        {filteredCurrencies.length === 0 ? (
                                            <div className="py-8 text-center text-[13px] text-gray-400">No currencies found</div>
                                        ) : filteredCurrencies.map(cur => {
                                            const isSelected = selectedCurrency?.currencyId === cur.currencyId
                                            return (
                                                <div
                                                    key={cur.currencyId}
                                                    onClick={() => {
                                                        setSelectedCurrency(cur)
                                                        setShowCurrencyDropdown(false)
                                                        setCurrencySearch('')
                                                        if (errors.currency) setErrors({ ...errors, currency: '' })
                                                    }}
                                                    className={`flex items-center justify-between px-4 py-2.5 cursor-pointer transition-colors hover:bg-orange-50/60 ${isSelected ? 'bg-orange-50 text-[#FF5200]' : ''}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        {cur.flag
                                                            ? <img src={cur.flag} className="w-7 h-5 rounded object-cover" alt="" />
                                                            : <span className="w-7 h-5 bg-gray-100 rounded text-[10px] flex items-center justify-center font-bold text-gray-400">{cur.currency_code}</span>
                                                        }
                                                        <div>
                                                            <p className="text-[13px] font-semibold text-gray-800 leading-none">{cur.currency_name}</p>
                                                            <p className="text-[11px] text-gray-400 mt-0.5">{cur.currency_code}</p>
                                                        </div>
                                                    </div>
                                                    <span className={`text-[15px] font-bold ${isSelected ? 'text-[#FF5200]' : 'text-gray-400'}`}>{cur.currency_symbol}</span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    }
                    {/* end currency col */}

                </div>{/* end grid grid-cols-2 */}

                {/* ── Negative Stock Toggle ────────────── */}
                <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-2xl border border-gray-100 mt-2">
                    <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center text-red-500 shrink-0 mt-0.5">
                            <Icon name="exclamation" size="md" />
                        </div>
                        <div>
                            <p className="text-[13px] font-bold text-gray-800 uppercase tracking-tight">
                                {localeText?.business?.settings?.isNegativeTransactionDisabled || 'Negative stock transactions'}
                            </p>
                            <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">
                                {localeText?.business?.settings?.isNegativeTransactionDisabledHint || 'By turning this on, You can prevent transactions that would result in negative stock.'}
                            </p>
                        </div>
                    </div>
                    <Toggle
                        value={field.isNegativeTransactionDisabled}
                        onChange={(val) => setField({ ...field, isNegativeTransactionDisabled: val })}
                        language={router.locale || 'en'}
                    />
                </div>

                {/* ── Contact Info Card ─────────────────── */}
                <div className="border border-orange-100 rounded-2xl overflow-hidden mt-2">
                    {/* Header Banner */}
                    <div className="flex items-start gap-3 bg-orange-50 px-4 py-3">
                        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center text-[#FF5200] shrink-0 mt-0.5">
                            <FileText size={16} />
                        </div>
                        <div>
                            <p className="text-[13px] font-bold text-gray-800">Contact Info</p>
                            <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">
                                {localeText?.business?.contactInfoHint || 'Shown on your invoices. Your address, phone and email appear on every invoice you send. All fields are optional.'}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-4 px-4 pt-4 pb-4">
                        {/* Address */}
                        <div>
                            <div className="flex items-center gap-2 mb-1.5">
                                <label className="text-[13px] font-semibold text-gray-700">
                                    {localeText?.business?.address || 'Business Address'}
                                </label>
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full">Optional</span>
                            </div>
                            <input
                                type="text"
                                value={(field as any).address || ''}
                                onChange={e => setField({ ...field, address: e.target.value } as any)}
                                placeholder="Street, area, city"
                                className="w-full h-[40px] px-3 text-[14px] border border-gray-200 rounded-lg outline-none focus:border-[#FF5200] focus:ring-1 focus:ring-[#FF5200]/20 transition-all bg-white"
                            />
                            <p className="text-[11px] text-gray-400 mt-1">Appears on invoices &amp; delivery notes</p>
                        </div>

                        {/* Phone + Email (2-col row) */}
                        <div className="grid grid-cols-2 gap-6">

                            {/* Phone — driven by the Country field above */}
                            <div>
                                <div className="flex items-center gap-2 mb-1.5">
                                    <label className="text-[13px] font-semibold text-gray-700">
                                        {localeText?.business?.phone || 'Phone'}
                                    </label>
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full">Optional</span>
                                </div>
                                <PhoneInput
                                    country={field.countryCode?.toLowerCase() || 'in'}
                                    value={(field as any).phoneNumber || ''}
                                    disableCountryGuess={true}
                                    countryCodeEditable={false}
                                    onChange={(value, data: any) => {
                                        setField((prev: any) => ({
                                            ...prev,
                                            phoneNumber: value,
                                            // keep countryCode in sync if user changes flag inside PhoneInput
                                            countryCode: data?.countryCode?.toUpperCase() || prev.countryCode
                                        }))
                                    }}
                                    placeholder={localeText?.customer?.form?.contact?.placeholder || 'Enter contact number'}
                                    inputClass="!w-full !h-[36px] !rounded-lg !text-[13px] !bg-gray-50 !border-gray-200 !text-neutral-800"
                                    buttonClass="!bg-transparent !border-none !rounded-l-lg hover:!bg-gray-100"
                                    dropdownClass="!rounded-lg !shadow-lg"
                                />

                                {((field as any).additionalPhoneNumber || []).map((phone: string, index: number) => (
                                    <div key={index} className="flex items-center gap-2 mt-2">
                                        <div className="flex-1">
                                            <PhoneInput
                                                country={field.countryCode?.toLowerCase() || 'in'}
                                                value={phone}
                                                disableCountryGuess={true}
                                                countryCodeEditable={false}
                                                onChange={(value) => {
                                                    const updated = [...((field as any).additionalPhoneNumber || [])]
                                                    updated[index] = value
                                                    setField({ ...field, additionalPhoneNumber: updated } as any)
                                                }}
                                                placeholder={localeText?.customer?.form?.contact?.placeholder || 'Enter contact number'}
                                                inputClass="!w-full !h-[36px] !rounded-lg !text-[13px] !bg-gray-50 !border-gray-200 !text-neutral-800"
                                                buttonClass="!bg-transparent !border-none !rounded-l-lg hover:!bg-gray-100"
                                                dropdownClass="!rounded-lg !shadow-lg"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const updated = [...((field as any).additionalPhoneNumber || [])]
                                                updated.splice(index, 1)
                                                setField({ ...field, additionalPhoneNumber: updated } as any)
                                            }}
                                            className="w-[40px] h-[40px] flex items-center justify-center rounded-lg border border-red-100 bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-500 transition-all shrink-0"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    onClick={() => setField({ ...field, additionalPhoneNumber: [...((field as any).additionalPhoneNumber || []), ''] } as any)}
                                    className="mt-2 flex items-center gap-1.5 text-[12px] font-bold text-[#FF5200] hover:opacity-80 transition-opacity"
                                >
                                    <Plus size={14} strokeWidth={3} />
                                    {localeText?.business?.add_another_phone || 'Add another phone'}
                                </button>

                                <p className="text-[11px] text-gray-400 mt-1">Shown on invoices</p>
                            </div>

                            {/* Email */}
                            <div>
                                <div className="flex items-center gap-2 mb-1.5">
                                    <label className="text-[13px] font-semibold text-gray-700">
                                        {localeText?.business?.email || 'Email'}
                                    </label>
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full">Optional</span>
                                </div>
                                <div className="relative">
                                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="email"
                                        value={(field as any).email || ''}
                                        onChange={e => setField({ ...field, email: e.target.value } as any)}
                                        placeholder="hello@business.com"
                                        className="w-full h-[40px] pl-9 pr-3 text-[13px] border border-gray-200 rounded-lg outline-none focus:border-[#FF5200] focus:ring-1 focus:ring-[#FF5200]/20 transition-all bg-white"
                                    />
                                </div>
                                <p className="text-[11px] text-gray-400 mt-1">Reply email</p>
                            </div>

                        </div>{/* end grid grid-cols-2 */}
                    </div>
                </div>


                {/* ── Attributes ───────────────────────── */}
                <div>
                    <div className="mb-1">
                        <h3 className="text-[14px] font-bold text-gray-800">
                            {localeText?.ExtraField?.attribute || 'Attribute'}
                        </h3>
                        <p className="text-[12px] text-gray-500 mt-0.5 leading-relaxed">
                            {localeText?.ExtraField?.attributeHint || 'Add Business Number, GST Number, Address, Email, Phone, Website, Business Hours, Licenses & Permits, and other relevant information, which will also appear on the generated invoice.'}
                        </p>
                    </div>

                    {/* Attribute rows */}
                    <div className="flex flex-col gap-2 mt-3">
                        {(field.attributes || []).map((attr, index) => (
                            <div key={index} className="flex items-stretch gap-2">
                                <div className="flex-1 flex flex-col gap-1 border border-gray-200 rounded-xl p-2 bg-white">
                                    <input
                                        type="text"
                                        placeholder="Attribute Name"
                                        value={attr.key}
                                        onChange={e => updateAttribute(index, e.target.value, attr.value)}
                                        className="w-full text-[13px] font-semibold text-gray-700 outline-none placeholder:text-gray-400 border-b border-gray-100 pb-1"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Attribute Value"
                                        value={attr.value}
                                        onChange={e => updateAttribute(index, attr.key, e.target.value)}
                                        className="w-full text-[13px] text-gray-500 outline-none placeholder:text-gray-300 pt-0.5"
                                    />
                                </div>
                                <button
                                    onClick={() => removeAttribute(index)}
                                    className="w-10 flex items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-500 transition-all shrink-0"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={addAttribute}
                        className="mt-3 flex items-center gap-1.5 text-[13px] font-bold text-[#FF5200] hover:opacity-80 transition-opacity"
                    >
                        <Plus size={15} strokeWidth={3} />
                        {localeText?.ExtraField?.addAttribute || 'Add Attribute'}
                    </button>

                    {/* Invoice Preview */}
                    <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden">
                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-100">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /></svg>
                            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">
                                {localeText?.ExtraField?.previewOnInvoice || 'Preview on Invoice'}
                            </span>
                        </div>


                        <div className="px-3 py-2 space-y-0.5 text-gray-600">
                            <div className="text-[13px] font-bold leading-normal">
                                {field?.name}
                            </div>
                            <div className="text-[12px] leading-normal">{field?.address}</div>
                            {(field as any)?.phoneNumber && <div className="text-[12px] leading-normal">+{field?.phoneNumber}</div>}
                            {((field as any)?.additionalPhoneNumber || []).map((phone: string, i: number) => phone ? (
                                <div key={`add-phone-${i}`} className="text-[12px] leading-normal">+{phone}</div>
                            ) : null)}
                            <div className="text-[12px] leading-normal">{field?.email}</div>

                            {previewLines?.map((line, i) => (
                                <div key={i} className="text-[12px] leading-normal">
                                    {line.key} - {line.value}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

            </div>
        </Modal>
    )
}
